/**
 * The browser's half of map protection: WebCrypto for both the key and the content.
 *
 * PBKDF2-HMAC-SHA256 and AES-256-GCM with a 12-byte nonce and the tag appended — the layout the
 * phone's native AES produces too, so a map protected on one opens on the other.
 */
import type { CipherKey, MapCipher } from '@orbit-mind/domain';

function keyOf(key: CipherKey): CryptoKey {
  return key.handle as CryptoKey;
}

/** A copy on a plain `ArrayBuffer`, the only buffer every WebCrypto implementation accepts. */
function buffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  return copy;
}

export const browserCipher: MapCipher = {
  deriveKey: async (password, salt, iterations) => {
    const material = await crypto.subtle.importKey(
      'raw',
      buffer(new TextEncoder().encode(password)),
      'PBKDF2',
      false,
      ['deriveKey'],
    );
    const key = await crypto.subtle.deriveKey(
      { name: 'PBKDF2', hash: 'SHA-256', salt: buffer(salt), iterations },
      material,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
    return { handle: key };
  },
  encrypt: async (key, plaintext, iv) =>
    new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: buffer(iv) },
        keyOf(key),
        buffer(plaintext),
      ),
    ),
  decrypt: async (key, data, iv) =>
    new Uint8Array(
      await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buffer(iv) }, keyOf(key), buffer(data)),
    ),
  randomBytes: (count) => crypto.getRandomValues(new Uint8Array(count)),
};
